import React, { useEffect, useState, useRef, useMemo } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Circle from 'ol/geom/Circle';
import { Style, Icon, Fill, Stroke } from 'ol/style';
import { fromLonLat } from 'ol/proj';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import PinchZoom from 'ol/interaction/PinchZoom';
import DragPan from 'ol/interaction/DragPan';
import { defaults as defaultControls, Zoom, Attribution, FullScreen } from 'ol/control';
import { defaults as defaultInteractions } from 'ol/interaction';
import { platformModifierKeyOnly } from 'ol/events/condition';
import { getImageUrl } from '../../../../services/asset';
import styles from "../adLocationMap.scss";
import { getWindowWidth, isMobileScreen } from '../../../../services/utils';
import { useFullscreenCenterOnExit } from '../../../AdFormPage/LocationSelector/hooks/useFullscreenCenterOnExit';

const zoom = new Zoom({
  className: styles.olZoom
})

const attribution = new Attribution({
  collapsed: true,
  className: styles.olAttribution
})

const fullScreen = new FullScreen({
  className: styles.olFullscreen
})



export const useOpenLayerMap = (adSummary) => {

  const [mapInstance, setMapInstance] = useState(null);
  const isMobile = isMobileScreen(getWindowWidth());

  // Settlement coordinates come with the ad detail (settlementLatitude / settlementLongitude)
  // and are the fallback position when the ad has no coordinates of its own.
  const settlementResource = useMemo(() => {
    if (!adSummary || !adSummary.settlementLatitude || !adSummary.settlementLongitude) return;

    return {
      latitude: adSummary.settlementLatitude,
      longitude: adSummary.settlementLongitude
    };
  }, [adSummary])


  useEffect(() => {
    if (!adSummary) {
      return;
    }

    const { longitude, latitude, isPreciseLocation } = adSummary;

    if (!settlementResource) return;

    // Use ad coordinates if available, otherwise fall back to settlement coordinates
    const coordinates = fromLonLat(
      longitude && latitude
        ? [longitude, latitude]
        : [settlementResource.longitude, settlementResource.latitude]
    );

    // Create feature based on isPreciseLocation
    let locationFeature: Feature;

    if (isPreciseLocation) {
      // Create a marker feature at the location
      locationFeature = new Feature({
        geometry: new Point(coordinates),
      });

      // Style the marker
      locationFeature.setStyle(
        new Style({
          image: new Icon({
            anchor: [0.5, 1],
            src: getImageUrl("pin.png"),
          }),
        })
      );
    } else {
      // Create a circle feature (radius in meters, approximately 500m)
      locationFeature = new Feature({
        geometry: new Circle(coordinates, 500),
      });

      // Style the circle
      locationFeature.setStyle(
        new Style({
          stroke: new Stroke({
            color: 'rgba(255, 0, 0, 0.8)',
            width: 2,
          }),
          fill: new Fill({
            color: 'rgba(255, 0, 0, 0.2)',
          }),
        })
      );
    }

    // Create a vector layer for the feature
    const vectorLayer = new VectorLayer({
      source: new VectorSource({
        features: [locationFeature],
      }),
    });

    const map = new Map({
      target: "ad-location-map",
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
            attributions: '© <a target="_blank" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          })
        }),
        vectorLayer,
      ],
      view: new View({
        center: coordinates,
        zoom: 15,
      }),
      controls: defaultControls({ attribution: false, zoom: false, rotate: false }).extend([
        zoom,
        attribution,
        !isMobile && fullScreen,
      ].filter(Boolean)),
      interactions: defaultInteractions({
        mouseWheelZoom: false,
        dragPan: !isMobile,
        pinchRotate: false // Disable rotation
      })
    });

    // On mobile, add custom DragPan that requires two fingers
    if (isMobile) {
      const dragPan = new DragPan({
        condition: function (event) {
          return this.getPointerCount() === 2 || platformModifierKeyOnly(event);
        }
      });
      map.addInteraction(dragPan);
    }

    // Add MouseWheelZoom that only works when Ctrl is pressed (unless in fullscreen)
    const mouseWheelZoom = new MouseWheelZoom({
      condition: (event) => {
        // In fullscreen, always allow mouse wheel zoom
        if (document.fullscreenElement) {
          return true;
        }
        // Outside fullscreen, require Ctrl key
        return platformModifierKeyOnly(event);
      }
    });
    map.addInteraction(mouseWheelZoom);

    // Add PinchZoom for two-finger zoom on touch devices
    const pinchZoom = new PinchZoom();
    map.addInteraction(pinchZoom);

    // Store map instance for the return button
    setMapInstance(map);

    return () => {
      map.setTarget(undefined);
    };
  }, [adSummary]);

  const handleReturnToLocation = () => {
    if (!(adSummary.longitude && adSummary.latitude) && !settlementResource) {
      return;
    }

    const coordinates = fromLonLat(
      adSummary.longitude && adSummary.latitude
        ? [adSummary.longitude, adSummary.latitude]
        : [settlementResource.longitude, settlementResource.latitude]
    );

    if (mapInstance && coordinates) {
      mapInstance.getView().animate({
        center: coordinates,
        zoom: 15,
        duration: 500
      });
    }
  };

  useFullscreenCenterOnExit(adSummary.longitude, adSummary.latitude, handleReturnToLocation)


  return {
    handleReturnToLocation
  }

}


// WEBPACK FOOTER //
// ./src/containers/AdDetailsPage/AdLocationMap/hooks/useOpenLayerMap.ts